/**
 * Iter 253z-11 · Performance Marketing · Accounts page.
 *
 * Manages ad-platform connections (Meta, later Google/LinkedIn/TikTok):
 * OAuth connect, manual system-user token, or demo mode seeding.
 * Also lists each synced ad account and lets an admin link it to a brand.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, PlugZap, Trash2, RefreshCw, Zap, Key, ExternalLink, Facebook, Chrome } from "lucide-react";
import { apiErrorMessage } from "@/lib/apiError";

export default function PerformanceAccounts() {
  const { activeWorkspaceId } = useAuth();
  const [conns, setConns] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [busy, setBusy] = useState(true);
  const [oauthConfigured, setOauthConfigured] = useState(false);
  const [tokenDialog, setTokenDialog] = useState(null); // conn_id
  const [tokenValue, setTokenValue] = useState("");
  const [savingToken, setSavingToken] = useState(false);
  const [syncingId, setSyncingId] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [googleSeeding, setGoogleSeeding] = useState(false);

  const load = async () => {
    if (!activeWorkspaceId) return;
    setBusy(true);
    try {
      const [c, a, b] = await Promise.all([
        api.get(`/workspaces/${activeWorkspaceId}/perf/connections`),
        api.get(`/workspaces/${activeWorkspaceId}/perf/ad-accounts`),
        api.get(`/workspaces/${activeWorkspaceId}/brands`).catch(() => ({ data: [] })),
      ]);
      setConns(c.data?.rows || []);
      setOauthConfigured(!!c.data?.meta_oauth_configured);
      setAccounts(a.data || []);
      setBrands(b.data || []);
    } catch (e) {
      toast.error("Failed to load");
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeWorkspaceId]);

  const startOAuth = async () => {
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/perf/meta/oauth-url`);
      window.location.href = data.url;
    } catch (e) {
      toast.error(apiErrorMessage(e, "OAuth is not configured on this server yet"));
    }
  };

  const createManual = async () => {
    try {
      const { data } = await api.post(`/workspaces/${activeWorkspaceId}/perf/connections`, {
        mode: "manual", provider: "meta", name: "Meta · Manual token",
      });
      toast.success("Connection created — paste your system-user token to activate");
      await load();
      setTokenDialog(data.id);
      setTokenValue("");
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };

  const seedDemo = async (provider = "meta") => {
    const setBusy = provider === "google" ? setGoogleSeeding : setSeeding;
    setBusy(true);
    try {
      await api.post(`/workspaces/${activeWorkspaceId}/perf/connections`, {
        mode: "demo", provider,
        name: `Demo ${provider === "google" ? "Google Ads" : "Meta"}`,
      });
      toast.success(`Demo ${provider === "google" ? "Google Ads" : "Meta"} data seeded`);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
    finally { setBusy(false); }
  };

  const saveToken = async () => {
    if (!tokenValue.trim()) { toast.error("Paste your access token"); return; }
    setSavingToken(true);
    try {
      await api.post(`/perf/connections/${tokenDialog}/token`, { access_token: tokenValue.trim() });
      toast.success("Token saved · syncing");
      setTokenDialog(null); setTokenValue("");
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Token rejected"));
    } finally { setSavingToken(false); }
  };

  const resync = async (id) => {
    setSyncingId(id);
    try {
      const { data } = await api.post(`/perf/connections/${id}/sync`);
      if (data.synced) toast.success(`Synced — ${data.accounts} account(s), ${data.campaigns} campaign(s)`);
      else toast.error(data.error === "live_meta_not_wired" ? "Live Meta sync isn't enabled yet — use demo mode or add META_APP_ID/META_APP_SECRET to the server." : data.error || "Sync failed");
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Sync failed")); }
    finally { setSyncingId(null); }
  };

  const deleteConn = async (c) => {
    if (!window.confirm(`Remove ${c.name}? This clears its accounts, campaigns and insights.`)) return;
    try {
      await api.delete(`/perf/connections/${c.id}`);
      toast.success("Removed");
      await load();
    } catch (e) { toast.error("Failed"); }
  };

  const linkBrand = async (accId, brandId) => {
    try {
      await api.patch(`/perf/ad-accounts/${accId}`, { linked_brand_id: brandId || null });
      toast.success(brandId ? "Linked" : "Unlinked");
      await load();
    } catch (e) { toast.error("Failed"); }
  };

  return (
    <>
      <PageHeader
        eyebrow="Performance Marketing"
        title="Ad accounts & connections"
        description="Connect Meta (Facebook + Instagram) via OAuth, paste a system-user token, or seed demo data to preview the module."
        testid="perf-accounts-header"
      />
      <div className="px-4 sm:px-6 md:px-10 pb-10 space-y-6">
        {/* Connect buttons */}
        <section className="border border-border p-5 space-y-5" data-testid="perf-connect-cards">
          {/* Meta */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Facebook className="w-5 h-5 text-primary" />
              <div className="font-bold text-sm">Meta (Facebook + Instagram)</div>
              <span className="ml-2 text-[10px] text-muted-foreground">
                {oauthConfigured ? <b className="text-emerald-600">OAuth configured ✓</b> : <b className="text-amber-600">OAuth not configured</b>}
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="border border-border p-3">
                <div className="text-xs font-semibold mb-1">Connect with Meta (OAuth)</div>
                <p className="text-[10px] text-muted-foreground mb-2">One-click flow. Requires a registered Meta app on the server.</p>
                <Button onClick={startOAuth} disabled={!oauthConfigured} className="rounded-none w-full h-8 text-xs" data-testid="perf-connect-oauth">
                  <PlugZap className="w-3 h-3 mr-1" /> Connect
                </Button>
              </div>
              <div className="border border-border p-3">
                <div className="text-xs font-semibold mb-1">Manual system-user token</div>
                <p className="text-[10px] text-muted-foreground mb-2">Paste a long-lived Meta system-user access token. <a href="https://business.facebook.com/business-settings" target="_blank" rel="noreferrer" className="underline inline-flex items-center gap-0.5">Get one <ExternalLink className="w-2.5 h-2.5" /></a></p>
                <Button variant="outline" onClick={createManual} className="rounded-none w-full h-8 text-xs" data-testid="perf-connect-manual">
                  <Key className="w-3 h-3 mr-1" /> Add token
                </Button>
              </div>
              <div className="border border-border p-3">
                <div className="text-xs font-semibold mb-1">Demo mode</div>
                <p className="text-[10px] text-muted-foreground mb-2">Seed realistic Meta ad accounts + campaigns + 30 days of insights.</p>
                <Button variant="outline" onClick={() => seedDemo("meta")} disabled={seeding} className="rounded-none w-full h-8 text-xs" data-testid="perf-connect-demo">
                  {seeding ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Zap className="w-3 h-3 mr-1" />}
                  {seeding ? "Seeding…" : "Seed Meta"}
                </Button>
              </div>
            </div>
          </div>

          {/* Google Ads */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Chrome className="w-5 h-5 text-primary" />
              <div className="font-bold text-sm">Google Ads</div>
              <span className="ml-2 text-[10px] text-muted-foreground">
                <b className="text-amber-600">Live API not wired yet</b>
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="border border-border p-3 opacity-60">
                <div className="text-xs font-semibold mb-1">Connect with Google (OAuth)</div>
                <p className="text-[10px] text-muted-foreground mb-2">Requires a Google Ads developer token + OAuth 2 app. <a href="https://developers.google.com/google-ads/api/docs/first-call/dev-token" target="_blank" rel="noreferrer" className="underline inline-flex items-center gap-0.5">Docs <ExternalLink className="w-2.5 h-2.5" /></a></p>
                <Button disabled className="rounded-none w-full h-8 text-xs" data-testid="perf-connect-google-oauth">
                  <PlugZap className="w-3 h-3 mr-1" /> Coming soon
                </Button>
              </div>
              <div className="border border-border p-3">
                <div className="text-xs font-semibold mb-1">Manual token</div>
                <p className="text-[10px] text-muted-foreground mb-2">Paste a Google Ads OAuth refresh token to poll accounts server-side.</p>
                <Button
                  variant="outline"
                  onClick={async () => {
                    try {
                      const { data } = await api.post(`/workspaces/${activeWorkspaceId}/perf/connections`, {
                        mode: "manual", provider: "google", name: "Google Ads · Manual token",
                      });
                      toast.success("Connection created — paste your refresh token");
                      await load();
                      setTokenDialog(data.id);
                      setTokenValue("");
                    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
                  }}
                  className="rounded-none w-full h-8 text-xs"
                  data-testid="perf-connect-google-manual"
                >
                  <Key className="w-3 h-3 mr-1" /> Add token
                </Button>
              </div>
              <div className="border border-border p-3">
                <div className="text-xs font-semibold mb-1">Demo mode</div>
                <p className="text-[10px] text-muted-foreground mb-2">Seed realistic Google Ads accounts (Search/Display/PMax/YouTube).</p>
                <Button variant="outline" onClick={() => seedDemo("google")} disabled={googleSeeding} className="rounded-none w-full h-8 text-xs" data-testid="perf-connect-google-demo">
                  {googleSeeding ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Zap className="w-3 h-3 mr-1" />}
                  {googleSeeding ? "Seeding…" : "Seed Google"}
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Connections list */}
        <section className="border border-border" data-testid="perf-connections-list">
          <div className="px-4 py-3 border-b border-border bg-muted/30">
            <div className="label-eyebrow">Connections</div>
          </div>
          {busy ? <div className="p-6 text-center text-xs text-muted-foreground">Loading…</div>
            : conns.length === 0 ? <div className="p-8 text-center text-xs text-muted-foreground">No connections yet. Use one of the cards above.</div>
            : (
              <table className="w-full text-xs">
                <thead className="bg-muted/20 text-[10px] uppercase text-muted-foreground">
                  <tr>
                    <th className="text-left px-3 py-2">Provider</th>
                    <th className="text-left px-3 py-2">Name</th>
                    <th className="text-left px-3 py-2">Mode</th>
                    <th className="text-left px-3 py-2">Status</th>
                    <th className="text-left px-3 py-2">Last synced</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {conns.map((c) => (
                    <tr key={c.id} data-testid={`perf-conn-row-${c.id}`}>
                      <td className="px-3 py-2 uppercase font-semibold">{c.provider}</td>
                      <td className="px-3 py-2">{c.name}</td>
                      <td className="px-3 py-2"><span className="chip text-[9px]">{c.mode}</span></td>
                      <td className="px-3 py-2">
                        <span className={`chip text-[9px] ${c.status === "connected" ? "chip-approved" : c.status === "error" ? "chip-rejected" : ""}`}>{c.status}</span>
                        {c.error && <div className="text-[9px] text-rose-600 italic mt-0.5">{c.error}</div>}
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">{c.last_synced_at?.slice(0, 16).replace("T", " ") || "—"}</td>
                      <td className="px-3 py-2 flex items-center gap-1 justify-end">
                        {c.mode === "manual" && !c.status?.includes("connected") && (
                          <Button size="sm" variant="outline" className="rounded-none h-7 text-[10px]" onClick={() => setTokenDialog(c.id)} data-testid={`perf-conn-token-${c.id}`}>
                            <Key className="w-3 h-3 mr-1" /> Paste token
                          </Button>
                        )}
                        <Button size="sm" variant="outline" className="rounded-none h-7 text-[10px]" onClick={() => resync(c.id)} disabled={syncingId === c.id} data-testid={`perf-conn-sync-${c.id}`}>
                          {syncingId === c.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                          Sync
                        </Button>
                        <Button size="sm" variant="ghost" className="rounded-none h-7 w-7 text-rose-600" onClick={() => deleteConn(c)} data-testid={`perf-conn-del-${c.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          }
        </section>

        {/* Ad accounts list */}
        <section className="border border-border" data-testid="perf-ad-accounts-list">
          <div className="px-4 py-3 border-b border-border bg-muted/30">
            <div className="label-eyebrow">Ad accounts</div>
          </div>
          {accounts.length === 0 ? (
            <div className="p-8 text-center text-xs text-muted-foreground">No ad accounts yet. Connect a provider or seed demo data first.</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-muted/20 text-[10px] uppercase text-muted-foreground">
                <tr>
                  <th className="text-left px-3 py-2">Provider</th>
                  <th className="text-left px-3 py-2">Ad account</th>
                  <th className="text-left px-3 py-2">External ID</th>
                  <th className="text-left px-3 py-2">Currency</th>
                  <th className="text-left px-3 py-2">Linked brand</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {accounts.map((a) => (
                  <tr key={a.id} data-testid={`perf-acc-row-${a.id}`}>
                    <td className="px-3 py-2 uppercase">{a.provider}</td>
                    <td className="px-3 py-2 font-medium">{a.name}</td>
                    <td className="px-3 py-2 font-mono text-[10px]">{a.external_id}</td>
                    <td className="px-3 py-2">{a.currency}</td>
                    <td className="px-3 py-2">
                      <select
                        value={a.linked_brand_id || ""}
                        onChange={(e) => linkBrand(a.id, e.target.value)}
                        className="h-7 border border-border rounded-none text-xs px-1"
                        data-testid={`perf-acc-brand-${a.id}`}
                      >
                        <option value="">— Not linked —</option>
                        {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>

      {tokenDialog && (
        <Dialog open onOpenChange={(v) => { if (!v) { setTokenDialog(null); setTokenValue(""); } }}>
          <DialogContent data-testid="perf-token-dialog">
            <DialogHeader><DialogTitle>Paste your Meta system-user access token</DialogTitle></DialogHeader>
            <div className="space-y-2">
              <Input
                type="password"
                placeholder="EAAB…"
                value={tokenValue}
                onChange={(e) => setTokenValue(e.target.value)}
                className="font-mono"
                data-testid="perf-token-input"
              />
              <p className="text-[10px] text-muted-foreground">
                Get this from Business Manager → System Users → your system user → Generate Token. Grant scopes: <code>ads_read, ads_management, business_management</code>. Stored encrypted at rest.
              </p>
            </div>
            <DialogFooter>
              <Button variant="ghost" className="rounded-none" onClick={() => setTokenDialog(null)}>Cancel</Button>
              <Button onClick={saveToken} disabled={savingToken} className="rounded-none" data-testid="perf-token-save">
                {savingToken ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Key className="w-3.5 h-3.5 mr-1.5" />}
                {savingToken ? "Saving…" : "Save & sync"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
